import type { AgenticLogicRuleDto } from "@intelligems/ig-types/dtos/agentic-logic/rules";
import type { AgenticLogicRulePublishJobDto } from "@intelligems/ig-types/dtos/agentic-logic/publish";
import type { AgenticLogicTemplateSummaryDto } from "@intelligems/ig-types/functions/agentic-logic/templates/list";
import { stripMigrationProvenanceMarker } from "@intelligems/ig-types/dtos/agentic-logic/migration-provenance";
import { IgAlert } from "@intelligems/ig-theme/IgAlert";
import { IgLoading } from "@intelligems/ig-theme/IgLoading";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { Box, Stack } from "@mui/material";

import { useAgenticLogicRule } from "@src/api/agentic-logic/get-agentic-logic-rule";
import { useAgenticLogicTemplates } from "@src/api/agentic-logic/list-agentic-logic-templates";
import { useUpdateAgenticLogicRule } from "@src/api/agentic-logic/update-agentic-logic-rule";

import { RuleSettingsFields } from "./RuleSettingsFields";
import { useUnpublishedRuleChanges } from "./useUnpublishedRuleChanges";
import { BUSY_HINT, type RuleOperationPhase } from "./ruleSaveApplyState";
import { useRuleSaveApplyFlow } from "./useRuleSaveApplyFlow";

import { TemplateSelectionLogicTab } from "../graph/TemplateSelectionLogicTab";
import { ruleWorkspaceKind } from "../surface/surface";
import { TemplateConfigConflictBanner } from "../template-config/TemplateConfigConflictBanner";
import { TemplateRuleAdvancedTab } from "../template-config/TemplateRuleAdvancedTab";
import { useTemplateRuleWorkspace } from "../template-config/useTemplateRuleWorkspace";
import { RuleDescriptionEditor } from "../rules/RuleDescriptionEditor";
import { AgenticLogicRuleWorkspacePage } from "./AgenticLogicRuleWorkspacePage";
import { RuleDetailPageView } from "./RuleDetailPageView";
import { DEFAULT_TEMPLATE_RULE_DETAIL_TAB } from "./ruleDetailTabs";
import { useRuleDetailTab } from "./useRuleDetailTab";
import { useRuleDetailByline } from "./useRuleDetailByline";
import { useAgenticLogicSurface } from "@src/features/ai/pages/agentic-logic/surface/useAgenticLogicSurface";
import { useIsAdvancedAgenticLogicEnabled } from "@src/hooks/feature-flags/useIsAdvancedAgenticLogicEnabled";
import { useRootPageCurrentCrumb } from "@src/utils/useRootPageCurrentCrumb";

const CenteredLoading = () => (
  <Stack alignItems="center" justifyContent="center" sx={{ height: "100%" }}>
    <IgLoading />
  </Stack>
);

export const CenteredErrorState = ({
  title,
  message,
}: {
  title: string;
  message: string;
}) => (
  <Stack
    alignItems="center"
    justifyContent="center"
    spacing={1}
    sx={{ height: "100%", px: 4, textAlign: "center" }}
  >
    <IgTypography variant="body1" fontWeight={600}>
      {title}
    </IgTypography>
    <IgTypography color="text.secondary" sx={{ maxWidth: 480 }}>
      {message}
    </IgTypography>
  </Stack>
);

/**
 * A template-managed rule in the SAME page frame as a code rule: one Selection
 * logic canvas, one Advanced tab. A merchant cannot tell from the URL which
 * flavour a rule is and must not meet two different pages at one route — only
 * the Advanced body differs, config JSON here and the code workspace there.
 */
const TemplateRulePage = ({
  rule,
  template,
  latestPublishJob,
}: {
  rule: AgenticLogicRuleDto;
  template: AgenticLogicTemplateSummaryDto;
  latestPublishJob: AgenticLogicRulePublishJobDto | null;
}) => {
  const surface = useAgenticLogicSurface();
  const updateRule = useUpdateAgenticLogicRule();
  const advancedEnabled = useIsAdvancedAgenticLogicEnabled();
  const [selectedTab, setSelectedTab] = useRuleDetailTab(
    DEFAULT_TEMPLATE_RULE_DETAIL_TAB,
    advancedEnabled,
    surface.tabParam
  );
  const byline = useRuleDetailByline(rule);
  const comparison = useUnpublishedRuleChanges(rule);

  const editor = useTemplateRuleWorkspace({
    mode: { kind: "edit", rule },
    template,
    latestPublishJob,
    onCreated: () => undefined,
  });
  const { workspace } = editor;
  // Names every term that keeps an edit from autosaving: an edit that cannot
  // be written for an unnamed reason is the defect this hint exists for.
  const resolveSaveBlockedHint = (): string | undefined => {
    if (workspace.hasParamsConflict) {
      return "Resolve the configuration conflict to save your changes.";
    }
    if (!workspace.isSourceValid) {
      return "Fix the configuration to save your changes.";
    }
    if (workspace.error) {
      return "Your changes could not be saved. Review the rule settings in Atlas, then try again.";
    }
    return undefined;
  };

  const checksOnly = editor.publishJob?.mode === "dry_run";
  const phase: RuleOperationPhase = !editor.isPublishing
    ? "idle"
    : checksOnly
      ? "checking"
      : "applying";

  const flow = useRuleSaveApplyFlow({
    rule,
    ruleName: workspace.name,
    usedIn: byline.usedIn,
    comparison,
    edits: {
      isDirty: workspace.isDirty,
      isSaving: editor.isSaving,
      canSave: workspace.canPersist && !editor.isPublishing,
      canSaveOnLeave: workspace.canPersist,
      saveBlockedHint: resolveSaveBlockedHint(),
      revision: [
        workspace.source,
        workspace.settings,
        workspace.name,
        workspace.isDirty,
      ],
      save: (onError) =>
        void editor.save("save_draft_only").then(() => undefined, onError),
      savedAtTs: rule.lastUpdateTs,
    },
    phase,
    apply: () => void editor.publish(),
    publishJob: editor.publishJob,
    // A builder copy is made from the rule row, which the autosave keeps
    // current, so there is no workspace to send.
    copyDraft: undefined,
    copyDisabledHint: editor.isPublishing ? BUSY_HINT : undefined,
    runChecks: null,
  });

  return (
    <>
      {flow.dialogs}
      <RuleDetailPageView
        backLabel={surface.backLabel}
        name={workspace.name}
        description={stripMigrationProvenanceMarker(rule.description)}
        eligibleSurfaces={byline.eligibleSurfaces}
        usedIn={byline.usedIn}
        isRenaming={updateRule.isPending}
        isSavingDescription={updateRule.isPending}
        selectedTab={selectedTab}
        onSelectedTabChange={setSelectedTab}
        onRename={async (name) => {
          await updateRule.mutateAsync({
            body: { name },
            pathParams: { ruleId: rule.id },
            queryParams: undefined,
          });
          // The config workspace persists the name alongside the params, so it
          // has to adopt the one the frame just wrote.
          workspace.syncPersistedName(name);
        }}
        onSaveDescription={async (description) => {
          await updateRule.mutateAsync({
            body: { description },
            pathParams: { ruleId: rule.id },
            queryParams: undefined,
          });
        }}
        onBack={flow.goBack}
        headerStatus={flow.status}
        advancedEnabled={advancedEnabled}
        // A template rule runs no checks strip; its publish lives in the
        // unpublished-draft callout.
        checks={null}
        banners={
          <>
            {workspace.conflictParams !== null ? (
              <TemplateConfigConflictBanner
                conflictParams={workspace.conflictParams}
                onTakeTheirs={() =>
                  workspace.resolveParamsConflict("take_theirs")
                }
                onKeepMine={() => workspace.resolveParamsConflict("keep_mine")}
              />
            ) : null}
          </>
        }
        actions={flow.actions}
        selectionLogic={<TemplateSelectionLogicTab rule={rule} />}
        // Edits live in the workspace and autosave with the params — one
        // draft, one Apply.
        displaySettings={
          <RuleSettingsFields
            settings={workspace.settings}
            saving={editor.isSaving || editor.isPublishing}
            onChange={workspace.setSettings}
          />
        }
        advanced={
          <TemplateRuleAdvancedTab
            ruleId={rule.id}
            editor={editor}
            descriptionEditor={
              <RuleDescriptionEditor
                description={rule.description}
                saving={updateRule.isPending}
                onSave={async (description) => {
                  await updateRule.mutateAsync({
                    body: { description },
                    pathParams: { ruleId: rule.id },
                    queryParams: undefined,
                  });
                }}
              />
            }
          />
        }
      />
    </>
  );
};

/** Resolves the rule's template before the page can render it. */
const TemplateRulePageLoader = ({
  rule,
  latestPublishJob,
}: {
  rule: AgenticLogicRuleDto;
  latestPublishJob: AgenticLogicRulePublishJobDto | null;
}) => {
  const surface = useAgenticLogicSurface();
  const templatesQuery = useAgenticLogicTemplates();

  if (templatesQuery.isLoading) {
    return <CenteredLoading />;
  }
  if (templatesQuery.isError) {
    return (
      <CenteredErrorState
        title="Templates could not be loaded"
        message={`We could not load the templates for this rule. Return to ${surface.title} and try again.`}
      />
    );
  }

  const template = templatesQuery.data?.templates.find(
    (candidate) => candidate.id === rule.templateId
  );
  if (!template) {
    return (
      <CenteredErrorState
        title="Template is not available"
        message={`This rule uses a template that is no longer available. Return to ${surface.title} to select another rule.`}
      />
    );
  }

  return (
    <TemplateRulePage
      key={rule.id}
      rule={rule}
      template={template}
      latestPublishJob={latestPublishJob}
    />
  );
};

export const AgenticLogicRulePage = ({ ruleId }: { ruleId: string }) => {
  const surface = useAgenticLogicSurface();
  const ruleQuery = useAgenticLogicRule(ruleId);
  useRootPageCurrentCrumb(ruleQuery.data?.rule?.name);

  if (ruleQuery.isLoading) {
    return <CenteredLoading />;
  }
  if (ruleQuery.isError || !ruleQuery.data?.rule) {
    return (
      <CenteredErrorState
        title="Rule could not be loaded"
        message={`We could not load this rule. Return to ${surface.title} and try again.`}
      />
    );
  }

  const { rule } = ruleQuery.data;

  switch (ruleWorkspaceKind(rule.managedBy)) {
    // Every rule state: the last gate run is the durable record of what
    // shipped, so it survives the rule leaving `draft`.
    case "template":
      return (
        <TemplateRulePageLoader
          rule={rule}
          latestPublishJob={ruleQuery.data.latestPublishJob}
        />
      );
    case "code":
      return <AgenticLogicRuleWorkspacePage rule={rule} />;
  }
};
